import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { BadgePercent } from "lucide-react";

export default function Promos() {
  const { user } = useAuth();
  const { t } = useLang();
  const [promos, setPromos] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [form, setForm] = useState({ code: "", percent_off: "", max_redemptions: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/coach/promo-codes").then((r) => setPromos(r.data)).catch(() => {});
    api.get("/coach/promo-codes/redemptions").then((r) => setRedemptions(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/coach/promo-codes", {
        code: form.code.trim(),
        percent_off: Number(form.percent_off),
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
      });
      toast.success(t("promo.created"));
      setForm({ code: "", percent_off: "", max_redemptions: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard"), end: true },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/promos", key: "promos", label: t("nav.promos") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  return (
    <Layout nav={nav}>
      <div data-testid="promos-page">
        <h1 className="text-3xl font-black uppercase tracking-tight mb-2">{t("promo.title")}</h1>
        <p className="text-muted-foreground mb-8">{t("promo.hint")}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-testid="promo-cards">
          {promos.map((p) => {
            const remaining = p.max_redemptions != null ? Math.max(0, p.max_redemptions - p.times_redeemed) : null;
            return (
              <div key={p.code} className="kc-card p-6" data-testid={`promo-card-${p.code}`}>
                <div className="flex items-center justify-between mb-3">
                  <BadgePercent className="w-6 h-6 text-primary" />
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${p.active ? "bg-green-500/15 text-green-500" : "bg-muted text-muted-foreground"}`}
                    data-testid={`promo-status-${p.code}`}>
                    {p.active ? t("promo.active") : "OFF"}
                  </span>
                </div>
                <p className="font-mono text-2xl font-black tracking-tight">{p.code}</p>
                <p className="text-primary font-bold text-lg mb-3" data-testid={`promo-discount-${p.code}`}>
                  {p.percent_off ? `-${p.percent_off}%` : p.amount_off ? `-€${(p.amount_off / 100).toFixed(0)}` : ""}
                </p>
                <p className="text-sm text-muted-foreground" data-testid={`promo-redeemed-${p.code}`}>
                  {p.times_redeemed} {t("promo.redeemed")}
                  {p.max_redemptions != null && ` · ${remaining} ${t("promo.remaining")} ${p.max_redemptions}`}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{t("promo.coachOnly")}</p>
              </div>
            );
          })}
        </div>

        {user?.is_admin && (
          <div className="kc-card p-6 mb-10" data-testid="promo-create-form">
            <p className="kc-eyebrow mb-4">{t("promo.createTitle")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <input data-testid="promo-code-input" className="kc-input uppercase" placeholder={t("promo.code")}
                value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              <input data-testid="promo-percent-input" className="kc-input" type="number" min="1" max="100" placeholder={t("promo.percent")}
                value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value })} />
              <input data-testid="promo-max-input" className="kc-input" type="number" min="1" placeholder={t("promo.max")}
                value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} />
            </div>
            <button data-testid="promo-create-submit" onClick={create}
              disabled={saving || !form.code.trim() || !form.percent_off} className="kc-btn-primary">
              {saving ? t("common.loading") : t("promo.create")}
            </button>
          </div>
        )}

        <div className="kc-card p-6" data-testid="promo-redemptions">
          <p className="kc-eyebrow mb-4">{t("promo.usedTitle")}</p>
          {redemptions.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="promo-redemptions-empty">{t("promo.emptyUsed")}</p>
          ) : (
            <div className="divide-y divide-border">
              {redemptions.map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-4 py-3" data-testid={`promo-redemption-row-${i}`}>
                  <div className="min-w-0">
                    <p className="font-bold truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm"><span className="font-mono font-bold text-primary">{r.code}</span> · {r.package}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""} ·{" "}
                      <span className={r.payment_status === "paid" ? "text-green-500 font-semibold" : "text-amber-500"}>
                        {r.payment_status === "paid" ? t("promo.paid") : t("promo.pending")}
                      </span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
